import { CancelRequest, StatefulRequest, StatefulResponse } from "build-ts/stateful_api";
import showToast from "../hooks/useCustomToast";
import { Header } from "build-ts/metadata";
import { AnalysisPlanProto } from "build-ts/analysis_plan";
import { AnalysisTreeNodeProto, AnalysisTreeProto } from "build-ts/analysis";
import { GetRecommendations_Request, GetRecommendations_Response } from "build-ts/interface";

/**
 * Delete Analysis tree by ID.
 *
 * @param {string} id - The ID of the tree to fetch.
 * @returns {Promise<any>} A promise that resolves to the fetched analysis tree data.
 * @throws {Error} Throws an error if there is an issue fetching the data or if the request fails.
 */
export const deleteAnalysisTreeByID = async (treeId: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_DELETE_ANALYSIS_TREE_BY_ID}/${treeId}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    if (response.status !== 204) {
      return null;
    }

    return response;
  } catch (error) {
    showToast("Error deleting analysis tree", "", "error");
  }
}; 

/**
 * Fetches Analysis Tree Headers.
 *
 * @returns {Promise<any>} A promise that resolves to the fetched analysis tree data.
 * @throws {Error} Throws an error if there is an issue fetching the data or if the request fails.
 */
export const getTreeHeaders = async (sortField: string, metricId: string[],
  pageSize: number, pageNumber: number, searchString: string, sortAscending?: boolean
) => {
  const metricIdsParam = encodeURIComponent(JSON.stringify(metricId));

  // If sortAscending is provided, use it. Otherwise, default to true for sortField "LAST_MODIFIED" and false for all other fields.
  const sortAsc = sortAscending !== undefined ? sortAscending : (sortField === "LAST_MODIFIED" ? false : true);
  
  try {
    const response = await fetch(
      `${process.env.REACT_APP_GET_ALL_TREE_HEADERS}?sortByField=${sortField}&sortAscending=${sortAsc}&metricIds=${metricIdsParam}&pageSize=${pageSize}&pageNumber=${pageNumber}&searchString=${searchString}`,
      {
        method: "GET",
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Error in fetching analysis tree headers", "", "error");
      return null;
    }

    const data = await response.json();
    return {headers: data.headers.map((header: any) => Header.fromJSON(header)), totalRows: data.total_rows};
  } catch (error) {
    showToast("Error in fetching analysis tree headers.", "", "error");
    return {headers: [], totalRows: 0};
  }
};

/**
 * Generates trees based on the provided analysis tree requests.
 *
 * @param {StatefulRequest} json - analysis tree requests.
 * @returns {Promise<any>} A promise that resolves to an array of generated trees.
 * @throws {Error} Throws an error if there is an issue generating trees or if the request fails.
 */
export const statefulAnalysisTree = async (json: StatefulRequest): Promise<any | null> => {
  const timeoutDuration = parseInt(process.env.REACT_APP_DEFAULT_TIMEOUT!, 10);

  const timeout = new Promise<Response>((_, reject) => 
    setTimeout(() => reject(new Error("Analysis Tree Generation Timeout!")), timeoutDuration)
  );

  try {
    // Use Promise.race to race the fetch against the timeout
    const response = await Promise.race([
      fetch(process.env.REACT_APP_STATEFUL_ANALYSIS_TREE!, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(StatefulRequest.toJSON(json)),
      }),
      timeout
    ]);

    if (!response.ok) {
      showToast("Analysis Tree Generation Failed", "", "error");
      return null;
    }
    const data = await response.json();
    return StatefulResponse.fromJSON(data);
  } catch (error) {
    showToast("Analysis Tree Generation Failed", "", "error");
    return null; // Return null for failed requests or timeout
  }
};

/**
 * Fetches analysis tree node based on the providxsed treeId and nodeId
 *
 * @param {CancelRequest} json - request structure to cancel transaction.
 * @returns {Promise<any>} A promise that resolves to the fetched analysis tree data.
 * @throws {Error} Throws an error if there is an issue fetching the data or if the request fails.
 */
export const cancelTransaction = async (json: CancelRequest) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_CANCEL_STATEFUL!,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",       
        },
        credentials: "include",
        body: JSON.stringify(CancelRequest.toJSON(json)),
      }
    );
    if (response.status === 200) {
      showToast("transaction cancelled", "", "success");
      return;
    }

    showToast("Failed to cancel transaction", "", "error");
    return;

  } catch (error) {
    showToast("Error in cancel transaction", "", "error");
  }
};

/**
 * Execute analysis plan
 * 
 * @param {AnalysisPlanProto} json - analysis plan
 * @returns {Promise<AnalysisTreeProto>} A promise that resolves to the executed analysis plan data.
 * @throws {Error} Throws an error if there is an issue executing the analysis plan or if the request fails.
 */
export const executeAnalysisPlan = async (proto: AnalysisPlanProto) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_EXECUTE_ANALYSIS_PLAN!,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(AnalysisPlanProto.toJSON(proto)),
      }
    );

    if (!response.ok) {
      showToast("Analysis Plan Execution Failed", "", "error");
      return null;
    }

    const data = await response.json();
    return AnalysisTreeProto.fromJSON(data);
  } catch (error) {
    showToast("Analysis Plan Execution Failed", "", "error");
    return null;
  }
};

/**
 * Fetches recommendation edits for the given request.
 *
 * @param {GetRecommendations_Request} request - The request object containing the necessary parameters.
 * @returns {Promise<RecommendationEditProto[]>} A promise that resolves to the fetched recommendation edits.
 * @throws {Error} Throws an error if there is an issue fetching the recommendation edits or if the request fails.
 */
export const getRecommendationEdits = async (request: GetRecommendations_Request, includeData: boolean = false) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_GET_RECOMMENDATION_EDITS! + `?includeData=${includeData}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(GetRecommendations_Request.toJSON(request)),
      }
    );

    if (!response.ok) {
      showToast("Recommendation Edits Fetch Failed", "", "error");
      return null;
    }

    const data = await response.json();
    return GetRecommendations_Response.fromJSON(data);
  } catch (error) {
    showToast("Recommendation Edits Fetch Failed", "", "error");
    return null;
  }
};

/**
 * Fetches KGObservation data for the given request.
 *
 * @param request - The request object containing the necessary parameters.
 * @returns {Promise<KGObservationDataProto>} A promise that resolves to the fetched KGObservation data.
 * @throws {Error} Throws an error if there is an issue fetching the KGObservation data or if the request fails.
 */
export const getNodeData = async (request: AnalysisTreeNodeProto) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_GET_NODE_DATA!,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(AnalysisTreeNodeProto.toJSON(request)),
      }
    );
    if (!response.ok) {
      showToast("KGObservation Data Fetch Failed", "", "error");
      return null;
    }
    const data = await response.json();
    return AnalysisTreeNodeProto.fromJSON(data);
  } catch (error) {
    showToast("Error in fetching KGObservation data", "", "error");
    return null;
  }
};

/**
 * Fetch the tree with node.
 * 
 * @param treeId: string - The ID of the tree.
 * @param includeNodes: boolean - boolean flag to include node.
 * @param includeStarredCommentary: boolean - boolean flag to include starred commentary for the tree.
 * @param includeAllCommentary: boolean - boolean flag to include all commentaries should be included.
 * @param includeSavedCommentary: boolean - boolean flag saved commentaries should be included.
 * @returns {Promise<AnalysisTreeProto>} A promise that resolves to the fetched tree data.
 * @throws {Error} Throws an error if there is an issue fetching the tree data or if the request fails.
 */
export const getTreeById = async (
  treeId: string, 
  includeNodes: boolean, 
  includeStarredCommentary: boolean, 
  includeAllCommentary: boolean, 
  includeSavedCommentary: boolean
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_GET_ANALYSIS_TREE_BY_ID}/${treeId}?includeNodes=${includeNodes}&includeStarredCommentary=${includeStarredCommentary}&includeAllCommentary=${includeAllCommentary}&includeSavedCommentary=${includeSavedCommentary}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    if (!response.ok) {
      showToast("Tree Data Fetch Failed", "", "error");
      return null;
    }
    const data = await response.json();
    return AnalysisTreeProto.fromJSON(data);
  } catch (error) {
    showToast("Error in fetching Tree Data", "", "error");
    return null;
  }
};

/**
 * Fetches the data for a tree node.
 * 
 * @param treeId: string - The ID of the tree.
 * @param nodeId: string - The ID of the node.
 * @param includeData: boolean - boolean flag to include node data for the tree node.
 * @param includeParentKGObservation: boolean - boolean flag to include parent kg observation of the node.
 * @returns {Promise<AnalysisTreeNodeProto>} A promise that resolves to the fetched tree node data.
 * @throws {Error} Throws an error if there is an issue fetching the tree node data or if the request fails.
 */
export const getTreeNodeData = async (
  treeId: string, 
  nodeId: string, 
  includeData: boolean, 
  includeParentKGObservation: boolean
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_GET_ANALYSIS_TREE_NODE}?treeId=${treeId}&nodeId=${nodeId}&includeData=${includeData}&includeParentKGObservation=${includeParentKGObservation}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    if (!response.ok) {
      showToast("TreeNode Data Fetch Failed", "", "error");
      return null;
    }
    const data = await response.json();
    return AnalysisTreeNodeProto.fromJSON(data);
  } catch (error) {
    showToast("Error in fetching TreeNode Data", "", "error");
    return null;
  }
};